import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowUpRight, FileText, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { searchNavigation, type NavigationGroup } from '@/lib/workspace-navigation'

export function WorkspaceSearch({ groups }: { groups: NavigationGroup[] }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const location = useLocation()
  const id = useId()
  const results = searchNavigation(groups, query)

  function openSearch() {
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setQuery('')
    setOpen(true)
  }

  useEffect(() => {
    function handleShortcut(event: globalThis.KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        // Do not open a second modal over a form or another modal interaction.
        const currentDialog = document.querySelector('[role="dialog"][data-state="open"]')
        if (currentDialog) {
          if (inputRef.current && currentDialog.contains(inputRef.current)) {
            event.preventDefault()
            inputRef.current.focus()
          }
          return
        }
        if (event.repeat) return
        event.preventDefault()
        returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        setQuery('')
        setOpen(true)
      }
    }
    window.addEventListener('keydown', handleShortcut)
    return () => window.removeEventListener('keydown', handleShortcut)
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [location.key])

  function moveResultFocus(event: KeyboardEvent<HTMLElement>) {
    if (event.nativeEvent.isComposing) return
    const links = Array.from(resultsRef.current?.querySelectorAll<HTMLAnchorElement>('a') ?? [])
    if (!links.length) return
    const currentIndex = links.indexOf(document.activeElement as HTMLAnchorElement)
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      links[(currentIndex + 1) % links.length].focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (currentIndex === 0) inputRef.current?.focus()
      else links[currentIndex < 0 ? links.length - 1 : currentIndex - 1].focus()
    } else if (event.key === 'Enter' && event.target === inputRef.current) {
      event.preventDefault()
      links[0].click()
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => nextOpen ? openSearch() : setOpen(false)}>
      <span ref={triggerRef}>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="gap-2 px-3 text-muted-foreground sm:min-w-52 sm:justify-start"
            aria-label="Search pages and actions"
            aria-keyshortcuts="Control+k Meta+k"
          >
            <Search aria-hidden="true" className="size-4 shrink-0" />
            <span>Search</span>
            <kbd aria-hidden="true" className="ml-auto hidden rounded border px-1.5 py-0.5 text-[10px] sm:inline">Ctrl / ⌘ K</kbd>
          </Button>
        </DialogTrigger>
      </span>
      <DialogContent
        className="max-w-xl gap-3 p-4 sm:p-6"
        onEscapeKeyDown={() => setOpen(false)}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          inputRef.current?.focus()
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          const previous = returnFocusRef.current
          if (previous?.isConnected && !previous.closest('[inert]')) previous.focus()
          else triggerRef.current?.querySelector('button')?.focus()
        }}
      >
        <DialogHeader className="pr-7">
          <DialogTitle>Search pages & actions</DialogTitle>
          <DialogDescription>Find a workspace page or open a tool to get started.</DialogDescription>
        </DialogHeader>
        <div onKeyDown={moveResultFocus}>
          <label htmlFor={`${id}-query`} className="sr-only">Search pages and actions</label>
          <div className="relative">
            <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-3 size-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              id={`${id}-query`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Try tasks, time off, or convert PDF"
              autoComplete="off"
              aria-describedby={`${id}-hint ${id}-count`}
              className="pl-9"
            />
          </div>
          <p id={`${id}-count`} role="status" aria-live="polite" className="mt-3 text-xs text-muted-foreground">
            {results.length} {results.length === 1 ? 'result' : 'results'} available
          </p>
          <div ref={resultsRef} className="mt-2 max-h-[min(50dvh,24rem)] overflow-y-auto overscroll-contain">
            {results.length ? (
              <ul aria-label="Search results" className="space-y-1">
                {results.map((result) => (
                  <li key={result.id}>
                    <Link
                      to={result.to}
                      onClick={() => setOpen(false)}
                      className="flex min-h-14 items-center gap-3 rounded-lg p-3 hover:bg-muted focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                    >
                      {result.kind === 'action'
                        ? <ArrowUpRight aria-hidden="true" className="size-4 shrink-0 text-primary" />
                        : <FileText aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{result.label}</span>
                        <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{result.description}</span>
                      </span>
                      <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">{result.kind === 'action' ? 'Open flow' : 'Page'}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="space-y-2 rounded-lg bg-muted/40 p-6 text-center">
                <p className="font-medium">No matching pages or actions</p>
                <p className="text-sm text-muted-foreground">Try another word. Only destinations available to your account are listed.</p>
                <Button type="button" variant="outline" size="sm" onClick={() => { setQuery(''); inputRef.current?.focus() }}>Clear search</Button>
              </div>
            )}
          </div>
        </div>
        <p id={`${id}-hint`} className="border-t pt-3 text-xs text-muted-foreground">Use ↑ ↓ or Tab to move, Enter to open, and Esc to close. Actions open their page for you to continue.</p>
      </DialogContent>
    </Dialog>
  )
}
